import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';

const TableCell = ({ isLoading, children, className = '' }) => {
  const { woodStyles } = useTheme();
  return (
    <td
      className={`px-6 py-4 transition-all duration-200 
        ${className} 
        ${woodStyles.text.primary} 
        ${isLoading ? 'blur-[2px] select-none' : ''}`}
    >
      {children}
    </td>
  );
};

const GameHistoryTable = ({ userId, gameType = 'all' }) => {
  const { woodStyles } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef(null);

  const fetchGames = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `/api/users/${userId}/games/history?page=${currentPage}&limit=10&gameType=${gameType}`
      );
      setGames(response.data.games);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, userId, gameType]);

  const handlePageChange = (newPage, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  useEffect(() => {
    setCurrentPage(1);
  }, [gameType]);

  return (
    <div
      ref={tableRef}
      className={`rounded-lg border-2 shadow-xl overflow-hidden 
        ${woodStyles.card.background} 
        ${woodStyles.card.border}`}
      style={{
        background: woodStyles.background,
        backgroundImage: woodStyles.backgroundImage
      }}
    >
      <div
        className={`px-4 py-2 border-b ${woodStyles.card.border}`}
      >
        <h2 className={`font-semibold ${woodStyles.text.primary}`}>
          Game History
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={woodStyles.surface.secondary}>
            <tr className={woodStyles.text.secondary}>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Game ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Opponent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Your Color
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Result
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Time Control
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${woodStyles.card.border}`}>
            {games.map((game) => (
              <tr
                key={game.id}
                className={`${woodStyles.surface.primary} hover:${woodStyles.card.hover} transition-colors`}
              >
                <TableCell isLoading={isLoading}>
                  <Link
                    to={`/game/${game.id}`}
                    className="text-blue-500 hover:text-blue-400"
                  >
                    {game.id.slice(5)}
                  </Link>
                </TableCell>
                <TableCell isLoading={isLoading}>
                  {game.opponent}
                </TableCell>
                <TableCell isLoading={isLoading}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${game.playerColor === 'white'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-800 text-gray-100'
                    }`}
                  >
                    {game.playerColor}
                  </span>
                </TableCell>
                <TableCell isLoading={isLoading}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${game.result === 'win'
                      ? 'bg-green-100 text-green-800'
                      : game.result === 'loss'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {game.result}
                  </span>
                </TableCell>
                <TableCell isLoading={isLoading}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${game.isAnarchy
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {game.isAnarchy ? 'Anarchy' : 'Normal'}
                  </span>
                </TableCell>
                <TableCell isLoading={isLoading}>
                  {game.timeControl}s
                </TableCell>
                <TableCell isLoading={isLoading}>
                  {new Date(game.date).toLocaleDateString()}
                </TableCell>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && games.length === 0 && (
          <div className={`text-center py-8 ${woodStyles.text.secondary}`}>
            No games played yet
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div
        className={`px-4 py-3 border-t ${woodStyles.card.border} 
          ${isLoading ? 'blur-[2px]' : ''} transition-all duration-200`}
      >
        <div className="flex items-center justify-between">
          {/* Pagination controls remain the same, but use woodStyles for button styling */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className={`text-sm ${woodStyles.text.secondary}`}>
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{pagination.pages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={(e) => handlePageChange(1, e)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md  ${woodStyles.text.secondary}
                    ${currentPage === 1
                      ? woodStyles.button.disabled
                      : woodStyles.button.secondary}
                    ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  «
                </button>
                <button
                  onClick={(e) => handlePageChange(Math.max(1, currentPage - 1), e)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-3 py-2 rounded-l-md  ${woodStyles.text.secondary}
                    ${currentPage === 1
                      ? woodStyles.button.disabled
                      : woodStyles.button.secondary}
                    ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  ‹
                </button>
                <span className={`relative inline-flex items-center px-4 py-2
                    ${woodStyles.text.primary}`}>
                  {currentPage}
                </span>
                <button
                  onClick={(e) => handlePageChange(Math.min(pagination.pages, currentPage + 1), e)}
                  disabled={currentPage === pagination.pages}
                  className={`relative inline-flex items-center px-3 py-2 rounded-r-md ${woodStyles.text.secondary}
                    ${currentPage === pagination.pages
                      ? woodStyles.button.disabled
                      : woodStyles.button.secondary}
                    ${currentPage === pagination.pages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  ›
                </button>
                <button
                  onClick={(e) => handlePageChange(pagination.pages, e)}
                  disabled={currentPage === pagination.pages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md ${woodStyles.text.secondary}
                    ${currentPage === pagination.pages
                      ? woodStyles.button.disabled
                      : woodStyles.button.secondary}
                    ${currentPage === pagination.pages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  »
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameHistoryTable;